import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import ReactConfetti from 'react-confetti';
import { FaAws } from 'react-icons/fa';
import { VscAzure } from 'react-icons/vsc';
import { GrOracle } from 'react-icons/gr';
import { RiShieldCheckLine, RiFlowChart, RiImage2Line, RiGitBranchLine, RiRadarLine, RiRocket2Line, RiExternalLinkLine } from 'react-icons/ri';
import { SiGooglecloud, SiAlibabacloud, SiTerraform, SiAnsible, SiOpentofu, SiPulumi } from 'react-icons/si';
import SEO from '../components/common/SEO';
import seoConfig from '../config/seo.json';

const TOTAL_REPORT_PAGES = 33;
const REPORT_PAGES = Array.from({ length: TOTAL_REPORT_PAGES }, (_, i) => `/images/architectures/ecommerce/report/images/page_${i + 1}.png`);

function AutonomousDeliverables({ isMobile }) {
  const [activeView, setActiveView] = useState('report');
  const [page, setPage] = useState(0);
  const deliverableTabs = [
    { id: 'report', label: 'Architecture Report', meta: `${TOTAL_REPORT_PAGES} pages` },
    { id: 'costs', label: 'Costing Sheet', meta: 'FinOps model' },
    { id: 'iac', label: 'Infrastructure as Code', meta: 'Pulumi output' },
  ];
  const isReport = activeView === 'report';

  useEffect(() => {
    if (activeView !== 'report') return;
    const timer = setInterval(() => {
      setPage((p) => (p + 1) % TOTAL_REPORT_PAGES);
    }, 3000); 
    return () => clearInterval(timer);
  }, [activeView]);

  return (
    <div style={{
      width: '100%',
      maxWidth: '1200px',
      margin: '0 auto',
      position: 'relative',
      background: '#08111f',
      borderRadius: isMobile ? '18px' : '28px',
      border: '1px solid rgba(148,163,184,0.24)',
      overflow: 'hidden',
      boxShadow: '0 44px 110px -34px rgba(2,6,23,0.5), 0 0 0 1px rgba(255,255,255,0.04)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(14,165,233,0.16), rgba(15,23,42,0) 34%, rgba(37,99,235,0.16) 100%)', pointerEvents: 'none' }} />
      {/* Header bar */}
      <div style={{
        position: 'relative',
        zIndex: 2,
        padding: isMobile ? '16px' : '18px 22px',
        background: 'rgba(15,23,42,0.82)',
        borderBottom: '1px solid rgba(148,163,184,0.18)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
          <div style={{ display: 'none' /* macOS dots */ }} />
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {deliverableTabs.map((tab, index) => {
              const active = activeView === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveView(tab.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: isMobile ? '9px 12px' : '10px 14px',
                    borderRadius: '10px',
                    border: active ? '1px solid rgba(56,189,248,0.48)' : '1px solid rgba(148,163,184,0.16)',
                    cursor: 'pointer',
                    fontSize: '0.82rem',
                    fontWeight: 800,
                    background: active ? 'rgba(14,165,233,0.16)' : 'rgba(255,255,255,0.04)',
                    color: active ? '#f8fafc' : '#94a3b8',
                    transition: 'all 0.2s ease',
                    boxShadow: active ? '0 10px 24px rgba(14,165,233,0.16)' : 'none',
                  }}
                >
                  <span style={{ color: active ? '#7dd3fc' : '#64748b', fontSize: '0.72rem' }}>{String(index + 1).padStart(2, '0')}</span>
                  <span>{tab.label}</span>
                  {!isMobile && <span style={{ color: active ? '#bae6fd' : '#64748b', fontWeight: 700, fontSize: '0.72rem' }}>{tab.meta}</span>}
                </button>
              );
            })}
          </div>
        </div>
        
        {isReport && (
          <div style={{ background: 'rgba(2,6,23,0.72)', color: '#e0f2fe', border: '1px solid rgba(125,211,252,0.22)', padding: '6px 12px', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 800 }}>
            PAGE {page + 1} OF {TOTAL_REPORT_PAGES}
          </div>
        )}
      </div>

      {/* Page Viewer */}
      <div style={{
        position: 'relative',
        zIndex: 1,
        height: isMobile ? '540px' : '820px',
        background: isReport ? 'linear-gradient(135deg, #dbeafe 0%, #f8fafc 48%, #e0f2fe 100%)' : '#020617',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isReport ? (isMobile ? '24px 16px 44px' : '58px 72px 64px') : 0,
        perspective: '2000px'
      }}>
        {isReport && (
          <>
            <div style={{ position: 'absolute', top: isMobile ? '22px' : '34px', left: isMobile ? '18px' : '46px', right: isMobile ? '18px' : '46px', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(14,165,233,0.36), transparent)' }} />
            <div style={{ position: 'absolute', bottom: isMobile ? '18px' : '28px', left: isMobile ? '18px' : '46px', right: isMobile ? '18px' : '46px', display: 'flex', justifyContent: 'center', gap: '7px' }}>
              {[0, 1, 2, 3, 4].map((dot) => (
                <span key={dot} style={{ width: dot === page % 5 ? '28px' : '7px', height: '7px', borderRadius: '999px', background: dot === page % 5 ? '#0284c7' : 'rgba(100,116,139,0.28)', transition: 'all 0.25s ease' }} />
              ))}
            </div>
          </>
        )}

        {isReport && (
          <AnimatePresence mode="wait">
            <motion.div
              key={page}
              initial={{ opacity: 0, rotateY: 54, x: 90, y: 16, scale: 0.94 }}
              animate={{ opacity: 1, rotateY: 0, x: 0, y: 0, scale: 1 }}
              exit={{ opacity: 0, rotateY: -54, x: -90, y: 12, scale: 0.94 }}
              transition={{ duration: 0.72, ease: [0.22, 1, 0.36, 1] }}
              style={{
                position: 'relative',
                width: '100%',
                maxWidth: '860px',
                height: '100%',
                boxShadow: '0 36px 80px -24px rgba(15,23,42,0.38)',
                background: 'white',
                borderRadius: '8px',
                overflow: 'hidden',
                transformStyle: 'preserve-3d',
                backfaceVisibility: 'hidden',
                border: '1px solid rgba(148,163,184,0.26)'
              }}
            >
              <img
                src={REPORT_PAGES[page]}
                alt={`Report Page ${page + 1}`}
                style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.34) 48%, rgba(255,255,255,0) 54%)', transform: 'translateX(-120%)', animation: 'reportSheen 3s ease-in-out infinite', pointerEvents: 'none' }} />
            </motion.div>
          </AnimatePresence>
        )}

        {activeView === 'costs' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            style={{ width: '100%', height: '100%', overflow: 'hidden', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <video autoPlay loop muted playsInline style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}>
              <source src="/images/architectures/ecommerce/report/costs.webm" type="video/webm" />
            </video>
          </motion.div>
        )}

        {activeView === 'iac' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            style={{ width: '100%', height: '100%', overflow: 'hidden', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <video autoPlay loop muted playsInline style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}>
              <source src="/images/architectures/ecommerce/report/iac.webm" type="video/webm" />
            </video>
          </motion.div>
        )}
      </div>

      {/* Progress Line */}
      {isReport && (
        <div style={{ width: '100%', height: '4px', background: 'rgba(148,163,184,0.2)', position: 'relative', zIndex: 2 }}>
          <motion.div 
            key={page}
            initial={{ width: 0 }}
            animate={{ width: '100%' }}
            transition={{ duration: 3, ease: "linear" }}
            style={{ height: '100%', background: 'linear-gradient(90deg, #38bdf8, #2563eb)' }}
          />
        </div>
      )}
    </div>
  );
}

function ZoomableImage({ src, alt }) {
  const [zoom, setZoom] = React.useState(1);
  const [offset, setOffset] = React.useState({ x: 0, y: 0 });
  const MIN = 1; const MAX = 4;
  const clampZoom = (z) => Math.min(MAX, Math.max(MIN, z));

  React.useEffect(() => { setZoom(1); setOffset({ x: 0, y: 0 }); }, [src]);

  const handleWheel = React.useCallback((e) => {
    e.preventDefault();
    setZoom((z) => {
      const next = clampZoom(z - e.deltaY * 0.001 * z);
      if (next === MIN) setOffset({ x: 0, y: 0 });
      return next;
    });
  }, []);

  return (
    <div
      onWheel={handleWheel}
      style={{ position: 'relative', background: '#1e293b', overflow: 'hidden', height: 'calc(90vh - 130px)', cursor: zoom > 1 ? 'grab' : 'default' }}
    >
      {/* image — always fits at 1×, zoom scales from there */}
      <motion.div
        drag={zoom > 1}
        dragConstraints={{ left: -800, right: 800, top: -500, bottom: 500 }}
        dragElastic={0.04}
        animate={{ scale: zoom, x: offset.x, y: offset.y }}
        transition={{ type: 'spring', stiffness: 320, damping: 32 }}
        style={{ position: 'absolute', inset: 0, transformOrigin: 'center center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        onDragEnd={(_, info) => setOffset(o => ({ x: o.x + info.offset.x, y: o.y + info.offset.y }))}
      >
        <AnimatePresence mode="wait">
          <motion.img
            key={src}
            src={src}
            alt={alt}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            draggable={false}
            style={{
              display: 'block',
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              userSelect: 'none',
              pointerEvents: 'none',
              background: 'white',
              position: 'absolute',
              inset: 0,
            }}
          />
        </AnimatePresence>
      </motion.div>

      {/* zoom controls */}
      <div style={{ position: 'absolute', bottom: '14px', right: '14px', zIndex: 20, display: 'flex', gap: '6px', alignItems: 'center' }}>
        {[
          { label: '−', fn: () => setZoom(z => { const n = clampZoom(z - 0.5); if (n === MIN) setOffset({ x: 0, y: 0 }); return n; }) },
          { label: `${Math.round(zoom * 100)}%`, fn: () => { setZoom(1); setOffset({ x: 0, y: 0 }); } },
          { label: '+', fn: () => setZoom(z => clampZoom(z + 0.5)) },
        ].map(({ label, fn }) => (
          <button key={label} type="button" onClick={fn}
            style={{ minWidth: '36px', height: '34px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(15,23,42,0.88)', color: 'white', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', padding: '0 10px' }}>
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

function useCountUp(target, duration = 700) {
  const [display, setDisplay] = React.useState(target);
  const prev = React.useRef(target);
  React.useEffect(() => {
    const start = prev.current;
    const diff = target - start;
    if (diff === 0) return;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(start + diff * e));
      if (p < 1) requestAnimationFrame(tick);
      else prev.current = target;
    };
    requestAnimationFrame(tick);
  }, [target, duration]);
  return display;
}

function CloudArchitect() {
  const [activeArchitecture, setActiveArchitecture] = useState(0);
  const [activeUseCase, setActiveUseCase] = useState(0);
  const [expandedArchitecture, setExpandedArchitecture] = useState(null);
  const [activePlatformFeature, setActivePlatformFeature] = useState(0);

  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
  const [windowHeight, setWindowHeight] = React.useState(window.innerHeight);
  React.useEffect(() => {
    const handler = () => { setWindowWidth(window.innerWidth); setWindowHeight(window.innerHeight); };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth < 1024;

  // ROI Calculator state
  const calcRef = useRef(null);
  const [roiArchitects, setRoiArchitects] = useState(10);
  const [roiRate, setRoiRate] = useState(150);
  const [roiProjects, setRoiProjects] = useState(20);
  const [roiTasks, setRoiTasks] = useState({ architecture: true, diagram: true, prd: true, designDoc: true, costing: true, iac: true });
  const [showCelebration, setShowCelebration] = useState(false);
  const [confettiActive] = useState(false);
  const [, setRoiTouched] = useState(false);

const TASK_DEFS = {
    architecture: { label: 'Architecture Design', trad: 24, agent: 3, icon: <RiFlowChart size={24} color="#38bdf8" /> },
    diagram:      { label: 'Diagram Generation',   trad: 8,  agent: 0.5, icon: <RiImage2Line size={24} color="#a78bfa" /> },
    prd:          { label: 'PRD',                  trad: 16, agent: 2,   icon: <RiGitBranchLine size={24} color="#34d399" /> },
    designDoc:    { label: 'Design Detail Doc',    trad: 20, agent: 3,   icon: <RiRocket2Line size={24} color="#fbbf24" /> },
    costing:      { label: 'Costing Calculation',  trad: 10, agent: 1,   icon: <RiRadarLine size={24} color="#f87171" /> },
    iac:          { label: 'Infrastructure as Code', trad: 40, agent: 5,   icon: <RiShieldCheckLine size={24} color="#38bdf8" /> },
  };

  const savedHrsPerProject = Object.entries(roiTasks)
    .filter(([, v]) => v)
    .reduce((acc, [k]) => acc + TASK_DEFS[k].trad - TASK_DEFS[k].agent, 0);

  const dollarSavedPerYear = Math.round(savedHrsPerProject * roiProjects * roiRate);
  const weeksSavedPerYear = Math.round((savedHrsPerProject * roiProjects) / 40);
  const totalTradHrs = Object.values(TASK_DEFS).reduce((a, t) => a + t.trad, 0);
  const selTradHrs = Object.entries(roiTasks).filter(([,v]) => v).reduce((a, [k]) => a + TASK_DEFS[k].trad, 0);
  const ttmReduction = Math.round((selTradHrs / totalTradHrs) * 100);

const totalSavings = dollarSavedPerYear;

  const animDollar = useCountUp(totalSavings);
  const animWeeks  = useCountUp(weeksSavedPerYear);
  const animTTM    = useCountUp(ttmReduction);
  const fmtLive = (n) => n >= 1000000 ? `$${(n/1000000).toFixed(1)}M` : `$${Math.round(n/1000)}K`;

  useEffect(() => { window.scrollTo(0, 0); }, []);
  const meta = seoConfig.pages.CloudArchitect;

  // Sync usecase with URL hash
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    const idx = useCases.findIndex(uc => uc.id === hash);
    if (idx !== -1) {
      setActiveUseCase(idx);
      setActiveArchitecture(0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const useCases = [
    {
      id: 'healthcare',
      label: 'Healthcare Patient Portal',
      cloud: 'Azure',
      cloudIcon: <VscAzure size={22} color="#0078D4" />,
      cloudBadgeBg: '#eff6ff',
      cloudBadgeBorder: 'rgba(0,120,212,0.25)',
      cloudBadgeText: '#1e40af',
      diagrams: [
        { id: 'hc-req',  src: '/images/architectures/healthcare_patient_portal/requirements.png',              title: 'Requirements', tag: 'Step 01', desc: 'HIPAA compliance scope, patient data sensitivity, RTO/RPO, regulatory constraints, and integration requirements defined upfront.' },
        { id: 'hc-des',  src: '/images/architectures/healthcare_patient_portal/design.png',                    title: 'Design',       tag: 'Step 02', desc: 'Azure-native architecture options — App Service, AKS, or serverless — with trade-off analysis against HIPAA and cost targets.' },
        { id: 'hc-com',  src: '/images/architectures/healthcare_patient_portal/compliance.png',                title: 'Compliance',   tag: 'Step 03', desc: 'Azure Well-Architected review across all five pillars with HIPAA BAA, PHI data handling, and audit logging validated at design time.' },
        { id: 'hc-log',  src: '/images/architectures/healthcare_patient_portal/architecture/logical.png',      title: 'Logical',      tag: 'Step 04', desc: 'Logical topology with patient data zones, identity boundaries via Entra ID, network segmentation, and PHI isolation.' },
        { id: 'hc-phy',  src: '/images/architectures/healthcare_patient_portal/architecture/physical.png',     title: 'Physical',     tag: 'Step 05', desc: 'Physical Azure deployment with region selection, availability zones, SKU right-sizing, and FinOps cost governance.' },
        { id: 'hc-dat',  src: '/images/architectures/healthcare_patient_portal/architecture/dataflow.png',     title: 'Data Flow',    tag: 'Step 06', desc: 'Patient data lineage from ingestion to storage — HL7/FHIR flows, API gateway routing, and audit trail mapping.' },
        { id: 'hc-sec',  src: '/images/architectures/healthcare_patient_portal/architecture/security.png',     title: 'Security',     tag: 'Step 07', desc: 'Zero-trust security model, Azure Defender, Key Vault secrets strategy, RBAC boundaries, and HIPAA-aligned policy-as-code.' },
        { id: 'hc-mig',  src: '/images/architectures/healthcare_patient_portal/architecture/migration.png',    title: 'Migration',    tag: 'Step 08', desc: 'Phased migration with data validation gates, PHI cutover controls, rollback plan, and compliance sign-off checkpoints.' },
      ],
    },
    {
      id: 'ecommerce',
      label: 'E-Commerce Platform Migration',
      reportLink: '/agents/cloud-architect/ecommerce-report',
      cloud: 'AWS',
      cloudIcon: <FaAws size={22} color="#FF9900" />,
      cloudBadgeBg: '#fff8f0',
      cloudBadgeBorder: 'rgba(255,153,0,0.25)',
      cloudBadgeText: '#92400e',
      diagrams: [
        { id: 'ec-req',  src: '/images/architectures/ecommerce/requirement.png',                title: 'Requirements', tag: 'Step 01', desc: 'Business requirements, constraints, RTO/RPO, compliance scope, and budget ceiling captured before any design begins.' },
        { id: 'ec-des',  src: '/images/architectures/ecommerce/design.png',                     title: 'Design',       tag: 'Step 02', desc: 'High-level architecture options with trade-off analysis — cost vs reliability vs performance — before committing to a topology.' },
        { id: 'ec-com',  src: '/images/architectures/ecommerce/compliance.png',                 title: 'Compliance',   tag: 'Step 03', desc: 'WAF review mapped to AWS/Azure/GCP pillars. HIPAA, PCI, GDPR, and internal policy guardrails validated at design time.' },
        { id: 'ec-log',  src: '/images/architectures/ecommerce/architecture/logical.png',       title: 'Logical',      tag: 'Step 04', desc: 'Logical architecture showing service boundaries, IAM model, network segmentation, and data classification zones.' },
        { id: 'ec-phy',  src: '/images/architectures/ecommerce/architecture/physical.png',      title: 'Physical',     tag: 'Step 05', desc: 'Physical topology with region placement, AZ distribution, instance types, and FinOps right-sizing recommendations.' },
        { id: 'ec-dat',  src: '/images/architectures/ecommerce/architecture/dataflow.png',      title: 'Data Flow',    tag: 'Step 06', desc: 'End-to-end data lineage and request flow — bottlenecks, latency hotspots, and operational dependencies surfaced.' },
        { id: 'ec-sec',  src: '/images/architectures/ecommerce/architecture/security.png',      title: 'Security',     tag: 'Step 07', desc: 'Zero-trust perimeter, WAF placement, secrets strategy, policy-as-code guardrails, and audit control mapping.' },
        { id: 'ec-mig',  src: '/images/architectures/ecommerce/architecture/migration.png',     title: 'Migration',    tag: 'Step 08', desc: 'Phased migration plan with cutover strategy, rollback gates, HA/DR validation, and human approval checkpoints.' },
      ],
    },
    {
      id: 'fleet',
      label: 'Fleet Management Platform',
      cloud: 'OCI',
      cloudIcon: <GrOracle size={20} color="#F80000" />,
      cloudBadgeBg: '#fff1f1',
      cloudBadgeBorder: 'rgba(248,0,0,0.2)',
      cloudBadgeText: '#991b1b',
      diagrams: [
        { id: 'fl-req',  src: '/images/architectures/fleet_management/requirement.png',                title: 'Requirements', tag: 'Step 01', desc: 'Fleet scale, vehicle telemetry volume, real-time tracking SLAs, regulatory requirements, and OCI region constraints defined upfront.' },
        { id: 'fl-des',  src: '/images/architectures/fleet_management/design.png',                     title: 'Design',       tag: 'Step 02', desc: 'OCI-native architecture options — Streaming, Functions, and Autonomous DB — with trade-off analysis against latency and cost targets.' },
        { id: 'fl-com',  src: '/images/architectures/fleet_management/compliance.png',                 title: 'Compliance',   tag: 'Step 03', desc: 'OCI Well-Architected review with data residency, telematics data governance, and operational compliance validated at design time.' },
        { id: 'fl-log',  src: '/images/architectures/fleet_management/architecture/logical.png',       title: 'Logical',      tag: 'Step 04', desc: 'Logical topology with vehicle ingestion layer, event streaming, compartment boundaries, and IAM policy model.' },
        { id: 'fl-phy',  src: '/images/architectures/fleet_management/architecture/physical.png',      title: 'Physical',     tag: 'Step 05', desc: 'Physical OCI deployment with AD placement, shape right-sizing, Autonomous DB tiers, and FinOps cost governance.' },
        { id: 'fl-dat',  src: '/images/architectures/fleet_management/architecture/dataflow.png',      title: 'Data Flow',    tag: 'Step 06', desc: 'Telemetry data flow from vehicle edge to OCI Streaming to analytics — latency hotspots and processing dependencies mapped.' },
        { id: 'fl-sec',  src: '/images/architectures/fleet_management/architecture/security.png',      title: 'Security',     tag: 'Step 07', desc: 'OCI Vault secrets, network security groups, zero-trust compartment model, audit logging, and policy-as-code guardrails.' },
        { id: 'fl-mig',  src: '/images/architectures/fleet_management/architecture/migration.png',     title: 'Migration',    tag: 'Step 08', desc: 'Phased migration from legacy fleet systems to OCI — cutover strategy, data validation gates, and rollback checkpoints.' },
      ],
    },
    {
      id: 'freight',
      label: 'Freight Telemetry Platform',
      cloud: 'Alibaba',
      cloudIcon: <SiAlibabacloud size={22} color="#FF6A00" />,
      cloudBadgeBg: '#fff7ed',
      cloudBadgeBorder: 'rgba(255,106,0,0.25)',
      cloudBadgeText: '#9a3412',
      diagrams: [
        { id: 'ft-req',  src: '/images/architectures/freight_telemetry/requirement.png',               title: 'Requirements', tag: 'Step 01', desc: 'Freight volume, IoT device scale, real-time tracking SLAs, cross-border data compliance, and Alibaba Cloud region constraints defined upfront.' },
        { id: 'ft-des',  src: '/images/architectures/freight_telemetry/design.png',                    title: 'Design',       tag: 'Step 02', desc: 'Alibaba Cloud-native options — IoT Platform, Message Queue, and AnalyticDB — with trade-off analysis against latency, cost, and scale targets.' },
        { id: 'ft-com',  src: '/images/architectures/freight_telemetry/compliance.png',                title: 'Compliance',   tag: 'Step 03', desc: 'Alibaba Cloud security review with cross-border data transfer controls, MLPS compliance, and operational guardrails validated at design time.' },
        { id: 'ft-log',  src: '/images/architectures/freight_telemetry/architecture/logical.png',      title: 'Logical',      tag: 'Step 04', desc: 'Logical topology with freight device ingestion layer, VPC segmentation, RAM identity boundaries, and data classification zones.' },
        { id: 'ft-phy',  src: '/images/architectures/freight_telemetry/architecture/physical.png',     title: 'Physical',     tag: 'Step 05', desc: 'Physical Alibaba Cloud deployment with region and zone selection, ECS instance right-sizing, reserved instances, and FinOps cost governance.' },
        { id: 'ft-dat',  src: '/images/architectures/freight_telemetry/architecture/dataflow.png',     title: 'Data Flow',    tag: 'Step 06', desc: 'Freight telemetry flow from IoT edge devices through Message Queue to AnalyticDB — latency hotspots and cross-region replication mapped.' },
        { id: 'ft-sec',  src: '/images/architectures/freight_telemetry/architecture/security.png',     title: 'Security',     tag: 'Step 07', desc: 'Zero-trust model with Alibaba Cloud WAF, KMS secrets, RAM policy-as-code, ActionTrail audit logging, and network ACL guardrails.' },
        { id: 'ft-mig',  src: '/images/architectures/freight_telemetry/architecture/migration.png',    title: 'Migration',    tag: 'Step 08', desc: 'Phased migration from legacy freight systems to Alibaba Cloud — dual-write proxy, shadow traffic replay, feature flag toggles, and auto-rollback controller.' },
      ],
    },
    {
      id: 'govt',
      label: 'Government Citizen Portal',
      cloud: 'GCP',
      cloudIcon: <SiGooglecloud size={20} color="#4285F4" />,
      cloudBadgeBg: '#eff6ff',
      cloudBadgeBorder: 'rgba(66,133,244,0.25)',
      cloudBadgeText: '#1e3a8a',
      diagrams: [
        { id: 'gv-req',  src: '/images/architectures/govt_citizen_portal/requirements.png',                   title: 'Requirements', tag: 'Step 01', desc: 'Citizen scale, accessibility mandates, data sovereignty requirements, FedRAMP/government compliance scope, and SLA targets defined upfront.' },
        { id: 'gv-des',  src: '/images/architectures/govt_citizen_portal/design.png',                         title: 'Design',       tag: 'Step 02', desc: 'GCP-native architecture options — Cloud Run, GKE, and Spanner — with trade-off analysis against compliance, cost, and availability targets.' },
        { id: 'gv-com',  src: '/images/architectures/govt_citizen_portal/compliance.png',                     title: 'Compliance',   tag: 'Step 03', desc: 'Google Cloud Well-Architected review with data residency controls, VPC Service Controls, and government policy guardrails validated at design time.' },
        { id: 'gv-log',  src: '/images/architectures/govt_citizen_portal/architectures/logical.png',          title: 'Logical',      tag: 'Step 04', desc: 'Logical topology with citizen identity layer via Identity Platform, service mesh boundaries, data classification zones, and IAM model.' },
        { id: 'gv-phy',  src: '/images/architectures/govt_citizen_portal/architectures/physical.png',         title: 'Physical',     tag: 'Step 05', desc: 'Physical GCP deployment with region and zone selection, machine type right-sizing, committed use discounts, and FinOps cost governance.' },
        { id: 'gv-dat',  src: '/images/architectures/govt_citizen_portal/architectures/dataflow.png',         title: 'Data Flow',    tag: 'Step 06', desc: 'Citizen data flow from portal ingestion through Pub/Sub to BigQuery — PII handling, audit trail, and data lineage mapped end-to-end.' },
        { id: 'gv-sec',  src: '/images/architectures/govt_citizen_portal/architectures/security.png',         title: 'Security',     tag: 'Step 07', desc: 'Zero-trust model with BeyondCorp, Cloud Armor WAF, Secret Manager, CMEK encryption, and policy-as-code via OPA/Forseti.' },
        { id: 'gv-mig',  src: '/images/architectures/govt_citizen_portal/architectures/migration.png',        title: 'Migration',    tag: 'Step 08', desc: 'Phased migration from legacy government systems to GCP — data validation gates, citizen service continuity, and compliance sign-off checkpoints.' },
      ],
    },
  ];

  const currentUseCase = useCases[activeUseCase];
  const architectures = currentUseCase.diagrams;

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setActiveArchitecture((current) => (current + 1) % architectures.length);
    }, 3200);
    return () => window.clearInterval(intervalId);
  }, [architectures.length, activeUseCase]);
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') setExpandedArchitecture(null);
      if (event.key === 'ArrowRight' && expandedArchitecture) {
        const i = architectures.findIndex(a => a.id === expandedArchitecture.id);
        setExpandedArchitecture(architectures[(i + 1) % architectures.length]);
      }
      if (event.key === 'ArrowLeft' && expandedArchitecture) {
        const i = architectures.findIndex(a => a.id === expandedArchitecture.id);
        setExpandedArchitecture(architectures[(i - 1 + architectures.length) % architectures.length]);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [expandedArchitecture, architectures]);

  const unifiedCapabilities = [
    {
      title: 'Architecture Desk, 24/7',
      desc: 'Bring architecture questions whenever you need them. Use the workspace for live design reviews, rapid requirement discovery, and architecture tradeoff discussions without waiting for a workshop.',
      icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z"/></svg>),
      tint: '#0ea5e9',
      bg: 'linear-gradient(135deg, rgba(14,165,233,0.18), rgba(37,99,235,0.1))',
    },
    {
      title: 'Well-Architected Reviews',
      desc: 'Run WAF assessments across security, reliability, performance, cost, and operational excellence with concrete remediation guidance tied directly to the proposed topology.',
      icon: <RiShieldCheckLine size={22} />,
      tint: '#2563eb',
      bg: 'linear-gradient(135deg, rgba(37,99,235,0.16), rgba(99,102,241,0.1))',
    },
    {
      title: 'Infrastructure as Code',
      desc: 'Generate and refine Terraform, OpenTofu, Pulumi, and automation artifacts from architecture intent, then keep diagrams and code aligned as designs evolve.',
      icon: <RiFlowChart size={22} />,
      tint: '#7c3aed',
      bg: 'linear-gradient(135deg, rgba(124,58,237,0.16), rgba(168,85,247,0.08))',
    },
    {
      title: 'Architecture Images & Diagrams',
      desc: 'Produce polished architecture visuals, topology diagrams, executive-ready design artifacts, and presentation assets that stay aligned with the technical design.',
      icon: <RiImage2Line size={22} />,
      tint: '#0891b2',
      bg: 'linear-gradient(135deg, rgba(8,145,178,0.16), rgba(6,182,212,0.08))',
    },
    {
      title: 'Multi-Cloud & Hybrid',
      desc: 'Design AWS, Azure, GCP, OCI, and Alibaba topologies, including cross-cloud networking, identity boundaries, on-prem integration, and phased hybrid migration patterns.',
      icon: <RiGitBranchLine size={22} />,
      tint: '#0f766e',
      bg: 'linear-gradient(135deg, rgba(15,118,110,0.16), rgba(20,184,166,0.08))',
    },
    {
      title: 'Security & Governance',
      desc: 'Ask for zero-trust patterns, WAF placement, IAM boundaries, policy-as-code guardrails, tagging standards, audit controls, and compliance-ready reference architectures.',
      icon: <RiRadarLine size={22} />,
      tint: '#1d4ed8',
      bg: 'linear-gradient(135deg, rgba(29,78,216,0.16), rgba(59,130,246,0.08))',
    },
    {
      title: 'Modernization & Reliability',
      desc: 'Plan landing zones, platform foundations, HA/DR, observability, resilience targets, and modernization paths from monoliths or legacy estates into cloud-native stacks.',
      icon: <RiRocket2Line size={22} />,
      tint: '#0369a1',
      bg: 'linear-gradient(135deg, rgba(3,105,161,0.16), rgba(14,165,233,0.08))',
    },
    {
      title: 'Stakeholder Alignment',
      desc: 'Turn architecture decisions into business language. Generate trade-off summaries, cost-vs-risk comparisons, and exec-ready briefs that help non-technical leadership understand what they are approving and why.',
      icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>),
      tint: '#0284c7',
      bg: 'linear-gradient(135deg, rgba(2,132,199,0.16), rgba(56,189,248,0.08))',
    },
    {
      title: 'Design Docs & ADRs',
      desc: 'Skip the blank page. Generate architecture decision records, HLD/LLD documents, and presentation decks directly from the design conversation — so the writing burden does not eat the architecture time.',
      icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>),
      tint: '#4f46e5',
      bg: 'linear-gradient(135deg, rgba(79,70,229,0.16), rgba(99,102,241,0.08))',
    },
  ];

  const platformFeatures = [
    {
      title: 'Automated WAF Compliance',
      desc: 'Immediately validate your multi-cloud topologies across the five foundational pillars—Operational Excellence, Security, Reliability, Performance, and Cost Optimization—directly within the conversational stream.',
      img: '/images/architectures/waf-compliance.png',
      alt: 'Well-Architected Framework Validation',
      tag: 'WAF Review',
    },
    {
      title: 'Request Flow & Lineage',
      desc: 'Map the entire data lineage and request flow across your infrastructure. The agent tracks node-by-node traversal to highlight bottlenecks, operational dependencies, and latency.',
      img: '/images/architectures/request-flow.png',
      alt: 'Request Flow Story',
      tag: 'Flow Analysis',
    },
    {
      title: 'Architecture + Execution Workspace',
      desc: 'Move from architecture discussion into execution immediately. Work through tradeoffs, then ask the agent to generate IaC, validate Terraform, produce architecture diagrams, document landing zones, or prepare implementation artifacts in the same workspace.',
      img: '/images/architectures/conversational-chat.png',
      alt: 'Eagentix Chat Execution Interface',
      tag: 'Execution Workspace',
    },
  ];

  const targetPersonas = [
    {
      title: 'CTO / VP Engineering',
      desc: 'Use it to pressure-test modernization choices, resilience posture, cloud operating model, and executive architecture direction before major spend is committed.',
    },
    {
      title: 'Platform & Cloud Teams',
      desc: 'Use it for landing zones, IAM boundaries, HA/DR patterns, observability standards, and implementation-ready IaC without waiting for a workshop cycle.',
    },
    {
      title: 'Enterprise Architects & CCoE',
      desc: 'Use it to standardize reference architectures, compare multi-cloud patterns, enforce policy-as-code, and keep architecture guidance consistent across programs.',
    },
  ];

  const workflowSteps = [
    {
      step: '01',
      title: 'Bring the problem',
      desc: 'Start with a migration plan, a compliance constraint, an architecture diagram, a workload profile, or a rough set of business requirements.',
      cloud: <FaAws size={20} color="#FF9900" title="AWS" />,
    },
    {
      step: '02',
      title: 'Interrogate the constraints',
      desc: 'The agent asks about uptime targets, latency, budget posture, security boundaries, hybrid dependencies, and regulatory conditions before finalizing design decisions.',
      cloud: <VscAzure size={20} color="#0078D4" title="Azure" />,
    },
    {
      step: '03',
      title: 'Generate architecture outputs',
      desc: 'Move from conversation into topology diagrams, WAF reviews, implementation notes, policy guardrails, and IaC-aligned architecture recommendations.',
      cloud: <SiGooglecloud size={18} color="#4285F4" title="GCP" />,
    },
    {
      step: '04',
      title: 'Escalate when needed',
      desc: 'If the decision is high-risk or organization-specific, route the work into expert review rather than pretending the agent should operate alone.',
      cloud: <GrOracle size={18} color="#F80000" title="OCI" />,
    },
  ];

  const deliverables = [
    'Architecture diagrams and topology visuals',
    'Well-Architected review findings',
    'Terraform, OpenTofu, Pulumi, and automation guidance',
    'Migration and modernization roadmaps',
    'HA/DR and resilience recommendations',
    'Security boundary and policy-as-code patterns',
    'Trade-off summaries and exec-ready briefs',
    'Architecture decision records (ADRs) and HLD/LLD docs',
  ];

  const frameworkSpecificity = [
    'AWS Well-Architected: six-pillar review across Operational Excellence, Security, Reliability, Performance Efficiency, Cost Optimization, and Sustainability.',
    'Azure Well-Architected: assess Reliability, Security, Cost Optimization, Operational Excellence, and Performance Efficiency against Azure-native patterns.',
    'Google Cloud Well-Architected: map workload decisions across security, reliability, performance, cost, and operational guidance for GCP environments.',
  ];

  const decisionBenefits = [
    {
      title: 'Faster than waiting for a workshop',
      desc: 'Run architecture reviews the moment a team has a question instead of queueing a consulting cycle just to start discovery.',
    },
    {
      title: 'More consistent than ad hoc reviews',
      desc: 'Apply the same policy, reliability, security, and cost reasoning across repeated decisions without depending on who happens to be available.',
    },
    {
      title: 'Built for delivery, not just opinions',
      desc: 'The agent produces diagrams, WAF artifacts, IaC-aligned implementation notes, and code-adjacent outputs — not just conversational advice.',
    },
  ];

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setActivePlatformFeature((current) => (current + 1) % platformFeatures.length);
    }, 3600);

    return () => window.clearInterval(intervalId);
  }, [platformFeatures.length]);

  return (
    <main style={{ paddingTop: '80px', minHeight: '100vh', background: '#fafafa', color: '#111827', fontFamily: '"General Sans", Inter, -apple-system, BlinkMacSystemFont, system-ui, sans-serif' }}>
      <style>{`
        @keyframes architecturePan {
          0% { object-position: 0% 0%; }
          50% { object-position: 100% 0%; }
          100% { object-position: 100% 100%; }
        }
        @keyframes presenterTrace {
          0% {
            left: 14%;
            top: 28%;
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          28% {
            left: 36%;
            top: 38%;
            opacity: 1;
          }
          52% {
            left: 58%;
            top: 30%;
            opacity: 1;
          }
          78% {
            left: 76%;
            top: 46%;
            opacity: 1;
          }
          100% {
            left: 88%;
            top: 35%;
            opacity: 0;
          }
        }
        @keyframes presenterBeam {
          0% {
            transform: translateX(-10%) translateY(6%);
            opacity: 0;
          }
          12% {
            opacity: 0.55;
          }
          50% {
            transform: translateX(4%) translateY(-4%);
            opacity: 0.7;
          }
          100% {
            transform: translateX(12%) translateY(3%);
            opacity: 0;
          }
        }
        @keyframes presenterCaption {
          0%, 18% {
            opacity: 0;
            transform: translateY(8px);
          }
          24%, 72% {
            opacity: 1;
            transform: translateY(0);
          }
          100% {
            opacity: 0;
            transform: translateY(-6px);
          }
        }
        @keyframes reportSheen {
          0%, 26% {
            transform: translateX(-130%);
            opacity: 0;
          }
          42% {
            opacity: 0.65;
          }
          72%, 100% {
            transform: translateX(130%);
            opacity: 0;
          }
        }
      `}</style>
      <SEO title={meta.title} description={meta.description} pathname="/agents/cloud-architect" agentKey="CloudArchitect" />

      {/* HERO */}
      <section style={{ padding: '100px 0 120px', background: '#000', position: 'relative', overflow: 'hidden', color: 'white', fontFamily: '"General Sans", sans-serif' }}>
        <video autoPlay loop playsInline muted style={{ position: 'absolute', top: '50%', left: '50%', width: '100%', height: '100%', objectFit: 'cover', transform: 'translate(-50%, -50%)', zIndex: 0 }}>
          <source src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260217_030345_246c0224-10a4-422c-b324-070b7c0eceda.mp4" type="video/mp4" />
        </video>
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', zIndex: 1 }} />
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px', position: 'relative', zIndex: 2 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '64px', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ flex: '1.2 1 0%', minWidth: '400px' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', padding: '6px 16px', background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '30px', fontSize: '0.85rem', fontWeight: 700, marginBottom: '24px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                <span style={{ display: 'inline-block', width: '6px', height: '6px', background: '#38bdf8', borderRadius: '50%', marginRight: '8px', boxShadow: '#38bdf8 0 0 10px' }} />
                Principal Cloud Architect Agent
              </div>
              <h1 style={{ fontSize: '3.75rem', marginBottom: '24px', color: 'white', fontWeight: 800, lineHeight: 1.15, letterSpacing: '-0.02em' }}>
                Design <span style={{ color: '#38bdf8' }}>Infinite Scale</span> with Autonomous Intelligence.
              </h1>
              <p style={{ fontSize: '1.25rem', color: '#cbd5e1', marginBottom: '40px', lineHeight: 1.6, maxWidth: '600px' }}>
                Shift-left your infrastructure strategy. The Cloud Architect Agent proactively enforces the Well-Architected Framework and FinOps principles right from the first discovery meeting.
              </p>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <a href="https://app.eagentix.com/register?reason=Requesting%20access%20to%20Cloud%20Architect%20Agent" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#38bdf8', color: '#0f172a', padding: '16px 32px', borderRadius: '12px', fontWeight: 700, fontSize: '1.05rem', textDecoration: 'none', transition: '0.2s', boxShadow: 'rgba(56,189,248,0.39) 0 4px 14px 0' }}>
                  Request Access Now
                </a>
              </div>
            </div>
            <div style={{ flex: '1 1 0%', minWidth: '320px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div style={{ background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.1)', padding: '32px', borderRadius: '24px' }}>
                <p style={{ fontSize: '0.75rem', fontWeight: 800, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '32px', textAlign: 'center' }}>Native Support for 5 Major Clouds</p>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '32px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <FaAws size={40} color="#FF9900" title="AWS" />
                  <VscAzure size={40} color="#0078D4" title="Azure" />
                  <SiGooglecloud size={36} color="#4285F4" title="Google Cloud" />
                  <GrOracle size={36} color="#F80000" title="Oracle Cloud" />
                  <SiAlibabacloud size={48} color="#FF6A00" title="Alibaba Cloud" />
                </div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.1)', padding: '32px', borderRadius: '24px' }}>
                <p style={{ fontSize: '0.75rem', fontWeight: 800, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '32px', textAlign: 'center' }}>Code Generation with FinOps (Shift-Left)</p>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '40px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <SiTerraform size={36} color="#844FBA" title="Terraform" />
                  <SiAnsible size={36} color="#EE0000" title="Ansible" />
                  <SiOpentofu size={40} color="#FFDA18" title="OpenTofu" />
                  <SiPulumi size={36} color="#8A3391" title="Pulumi" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TOPOLOGY SHOWCASE */}
      <section id="usecases" style={{ padding: isMobile ? '48px 0' : '80px 0', background: 'white', borderTop: '1px solid #f3f4f6' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: '24px', marginBottom: '40px' }}>
            <div>
              {/* usecase switcher */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
                {useCases.map((uc, i) => (
                  <div key={uc.id} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <motion.button
                      type="button"
                      onClick={() => {
                        setActiveUseCase(i);
                        setActiveArchitecture(0);
                        window.history.replaceState(null, '', `#${uc.id}`);
                      }}
                      whileTap={{ scale: 0.96 }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '8px 16px', borderRadius: '999px', cursor: 'pointer',
                        border: i === activeUseCase ? 'none' : '1px solid #e2e8f0',
                        background: i === activeUseCase ? '#0f172a' : 'white',
                        color: i === activeUseCase ? 'white' : '#64748b',
                        fontSize: '0.82rem', fontWeight: 700,
                        boxShadow: i === activeUseCase ? '0 4px 14px rgba(15,23,42,0.18)' : 'none',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {uc.cloudIcon}
                      {uc.label}
                    </motion.button>
                    {/* share button */}
                    <motion.button
                      type="button"
                      title={`Copy link to ${uc.label}`}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => {
                        const url = `${window.location.origin}${window.location.pathname}#${uc.id}`;
                        navigator.clipboard.writeText(url).then(() => {
                          // brief visual feedback via title attr — no extra state needed
                        });
                      }}
                      style={{ width: '30px', height: '30px', borderRadius: '50%', border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', flexShrink: 0 }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
                        <polyline points="16 6 12 2 8 6"/>
                        <line x1="12" y1="2" x2="12" y2="15"/>
                      </svg>
                    </motion.button>
                  </div>
                ))}
              </div>
              <span style={{ color: '#0071e3', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', fontSize: '0.85rem' }}>{currentUseCase.label}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginTop: '12px', marginBottom: '10px', flexWrap: 'wrap' }}>
                <h2 style={{ fontSize: isMobile ? '1.6rem' : '2.5rem', fontWeight: 800, color: '#111827', margin: 0, letterSpacing: '-0.02em' }}>From Requirements to Migration</h2>
                {currentUseCase.reportLink && (
                  <Link 
                    to={currentUseCase.reportLink}
                    style={{ 
                      display: 'inline-flex', alignItems: 'center', gap: '8px', 
                      padding: '8px 18px', borderRadius: '12px', 
                      background: 'rgba(56,189,248,0.12)', border: '1px solid rgba(56,189,248,0.35)',
                      color: '#0369a1', fontSize: '0.88rem', fontWeight: 700,
                      textDecoration: 'none', transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => { e.target.style.background = 'rgba(56,189,248,0.18)'; e.target.style.transform = 'translateY(-1px)'; }}
                    onMouseLeave={(e) => { e.target.style.background = 'rgba(56,189,248,0.12)'; e.target.style.transform = 'translateY(0)'; }}
                  >
                    View Full 33-Page Report
                    <RiExternalLinkLine size={18} />
                  </Link>
                )}
              </div>
              <p style={{ fontSize: '1.05rem', color: '#6b7280', lineHeight: 1.6, maxWidth: '560px', margin: 0 }}>Every diagram the agent produces for a single use case — scroll through all 8 stages.</p>
            </div>
            {/* nav arrows */}
            <div style={{ display: 'flex', gap: '10px', flexShrink: 0 }}>
              <motion.button
                type="button"
                aria-label="Previous diagram"
                onClick={() => setActiveArchitecture((p) => (p - 1 + architectures.length) % architectures.length)}
                whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.94 }}
                style={{ width: '44px', height: '44px', borderRadius: '50%', border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(15,23,42,0.06)' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
              </motion.button>
              <motion.button
                type="button"
                aria-label="Next diagram"
                onClick={() => setActiveArchitecture((p) => (p + 1) % architectures.length)}
                whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.94 }}
                style={{ width: '44px', height: '44px', borderRadius: '50%', border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(15,23,42,0.06)' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
              </motion.button>
            </div>
          </div>

          {/* stage pill tabs */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '28px' }}>
            {architectures.map((arch, index) => (
              <motion.button
                key={arch.id}
                type="button"
                onClick={() => setActiveArchitecture(index)}
                whileTap={{ scale: 0.96 }}
                style={{
                  padding: '6px 14px',
                  borderRadius: '999px',
                  border: index === activeArchitecture ? 'none' : '1px solid #e2e8f0',
                  background: index === activeArchitecture ? 'linear-gradient(90deg, #0ea5e9, #2563eb)' : 'white',
                  color: index === activeArchitecture ? 'white' : '#64748b',
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  letterSpacing: '0.04em',
                  transition: 'all 0.2s ease',
                }}
              >
                {arch.tag} · {arch.title}
              </motion.button>
            ))}
          </div>

          {/* main image viewer */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            style={{ position: 'relative', borderRadius: '24px', border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 20px 48px -16px rgba(15,23,42,0.12)', background: '#f8fafc' }}
          >
            {/* drag hint overlay */}
            <div style={{ position: 'absolute', top: '16px', right: '16px', zIndex: 10, display: 'flex', gap: '8px' }}>
              <div style={{ padding: '6px 12px', borderRadius: '999px', background: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(8px)', color: 'white', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
                drag or use arrows
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
              </div>
            </div>

            <motion.div
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.12}
              onDragEnd={(e, info) => {
                if (info.offset.x < -60) setActiveArchitecture((p) => (p + 1) % architectures.length);
                if (info.offset.x > 60) setActiveArchitecture((p) => (p - 1 + architectures.length) % architectures.length);
              }}
              style={{ cursor: 'grab' }}
              whileDrag={{ cursor: 'grabbing' }}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={architectures[activeArchitecture].id}
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -40 }}
                  transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                >
                  <motion.button
                    type="button"
                    aria-label={`Expand ${architectures[activeArchitecture].title}`}
                    onClick={() => setExpandedArchitecture(architectures[activeArchitecture])}
                    style={{ width: '100%', display: 'block', padding: 0, border: 'none', background: 'none', cursor: 'zoom-in', position: 'relative' }}
                  >
                    <img
                      key={architectures[activeArchitecture].id}
                      src={architectures[activeArchitecture].src}
                      alt={architectures[activeArchitecture].title}
                      style={{
                        width: '100%',
                        height: isMobile ? '260px' : isTablet ? '380px' : '520px',
                        objectFit: architectures[activeArchitecture].src.includes('/architecture/') ? 'cover' : 'contain',
                        objectPosition: architectures[activeArchitecture].src.includes('/architecture/') ? '0% 0%' : 'center top',
                        background: 'white',
                        display: 'block',
                        animation: architectures[activeArchitecture].src.includes('/architecture/') ? 'architecturePan 18s ease-in-out infinite alternate' : 'none',
                      }}
                    />
                    {/* cloud badge bottom-left */}
                    <div style={{ position: 'absolute', bottom: '16px', left: '16px', display: 'flex', alignItems: 'center', gap: '7px', padding: '7px 12px', background: currentUseCase.cloudBadgeBg, backdropFilter: 'blur(8px)', border: `1px solid ${currentUseCase.cloudBadgeBorder}`, borderRadius: '10px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', pointerEvents: 'none' }}>
                      {currentUseCase.cloudIcon}
                      <span style={{ fontSize: '0.7rem', fontWeight: 800, color: currentUseCase.cloudBadgeText, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{currentUseCase.cloud} Architecture</span>
                    </div>
                  </motion.button>
                </motion.div>
              </AnimatePresence>
            </motion.div>

            {/* bottom info bar */}
            <div style={{ padding: '18px 24px', borderTop: '1px solid #e5e7eb', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                  <span style={{ padding: '3px 10px', borderRadius: '999px', background: 'linear-gradient(90deg, #0ea5e9, #2563eb)', color: 'white', fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.08em' }}>
                    {architectures[activeArchitecture].tag}
                  </span>
                  <h4 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: '#0f172a' }}>
                    {architectures[activeArchitecture].title}
                  </h4>
                </div>
                <p style={{ margin: 0, fontSize: '0.88rem', color: '#64748b', lineHeight: 1.55, maxWidth: '680px' }}>
                  {architectures[activeArchitecture].desc}
                </p>
              </div>
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8', fontWeight: 600, flexShrink: 0 }}>
                {activeArchitecture + 1} / {architectures.length}
              </p>
            </div>
          </motion.div>
        </div>
      </section>


      {/* DELIVERABLES + SAFETY */}
      <section style={{ padding: isMobile ? '48px 0' : '84px 0', background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)', borderTop: '1px solid #eef2f7', borderBottom: '1px solid #eef2f7' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <div style={{ textAlign: 'center', margin: '0 auto', maxWidth: '850px', marginBottom: '56px' }}>
            <span style={{ color: '#0071e3', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', fontSize: '0.85rem' }}>Autonomous Deliverables</span>
            <h2 style={{ fontSize: isMobile ? '1.6rem' : '2.6rem', fontWeight: 800, color: '#0f172a', margin: '16px 0 16px', letterSpacing: '-0.03em' }}>
              High-Fidelity Engineering Artifacts
            </h2>
            <p style={{ fontSize: '1.08rem', color: '#475569', lineHeight: 1.75, margin: 0 }}>
              The agent doesn't just chat; it produces production-grade architecture documents, WAF reviews, and implementation roadmaps. See it in action.
            </p>
          </div>

          <div 
            id="artifact-container"
            style={{ position: 'relative' }}
          >
            <AutonomousDeliverables isMobile={isMobile} />
          </div>
        </div>
      </section>

      {/* ROI CALCULATOR */}
      <section ref={calcRef} style={{ padding: isMobile ? '24px 0 64px' : '40px 0 100px', background: '#000', position: 'relative', overflow: 'hidden', color: 'white' }}>
        <video autoPlay loop playsInline muted style={{ position: 'absolute', top: '50%', left: '50%', width: '100%', height: '100%', objectFit: 'cover', transform: 'translate(-50%, -50%)', zIndex: 0 }}>
          <source src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260217_030345_246c0224-10a4-422c-b324-070b7c0eceda.mp4" type="video/mp4" />
        </video>
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1 }} />
        <div style={{ maxWidth: '960px', margin: '0 auto', padding: '0 24px', position: 'relative', zIndex: 2 }}>

          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#ffffff', textAlign: 'center', marginBottom: '32px', letterSpacing: '-0.02em' }}>ROI Calculator</h2>

          {/* LIVE SAVINGS BANNER */}
          <div style={{ background: 'rgba(56,189,248,0.07)', border: '1px solid rgba(56,189,248,0.25)', borderRadius: '28px', padding: isMobile ? '32px 20px' : '48px 56px', marginBottom: '40px', textAlign: 'center' }}>
            <div style={{ fontSize: isMobile ? '0.85rem' : '0.95rem', fontWeight: 700, color: '#7dd3fc', textTransform: 'uppercase', letterSpacing: '3px', marginBottom: '12px' }}>Your Annual Savings</div>
            <div style={{ fontSize: isMobile ? '5rem' : '9rem', fontWeight: 900, color: '#ffffff', lineHeight: 0.9, letterSpacing: '-0.04em', marginBottom: '12px', fontVariantNumeric: 'tabular-nums' }}>
              {fmtLive(animDollar)}
            </div>
            <div style={{ fontSize: '1rem', color: '#cbd5e1', marginBottom: '32px' }}>per year — personalise with the controls below</div>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: isMobile ? '24px' : '64px', flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: isMobile ? '2.5rem' : '3.5rem', fontWeight: 900, color: '#fbbf24', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{animWeeks}</div>
                <div style={{ fontSize: '1rem', color: '#e2e8f0', fontWeight: 600, marginTop: '6px' }}>weeks saved / year</div>
              </div>
              <div style={{ width: isMobile ? '100%' : '1px', height: isMobile ? '1px' : '72px', background: 'rgba(255,255,255,0.12)' }} />
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: isMobile ? '2.5rem' : '3.5rem', fontWeight: 900, color: '#34d399', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{animTTM}%</div>
                <div style={{ fontSize: '1rem', color: '#e2e8f0', fontWeight: 600, marginTop: '6px' }}>faster time-to-market</div>
              </div>
            </div>
          </div>

          <p style={{ textAlign: 'center', fontSize: isMobile ? '0.9rem' : '0.95rem', color: '#ffffff', marginBottom: '28px', marginTop: '-20px' }}>
            ↓ Set your team size and toggle tasks below. Number updates live.
          </p>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '24px' }}>
            <div style={{ textAlign: 'center', padding: '12px 20px', background: 'rgba(56,189,248,0.1)', borderRadius: '12px', border: '1px solid rgba(56,189,248,0.2)' }}>
              <div style={{ fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Typical architect hourly rate</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#38bdf8' }}>$150/hr</div>
            </div>
            <div style={{ textAlign: 'center', padding: '12px 20px', background: 'rgba(167,139,250,0.1)', borderRadius: '12px', border: '1px solid rgba(167,139,250,0.2)' }}>
              <div style={{ fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Avg hours saved / project</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#a78bfa' }}>~98 hrs</div>
            </div>
            <div style={{ textAlign: 'center', padding: '12px 20px', background: 'rgba(52,211,153,0.1)', borderRadius: '12px', border: '1px solid rgba(52,211,153,0.2)' }}>
              <div style={{ fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Break-even timeline</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#34d399' }}>&lt; 2 weeks</div>
            </div>
          </div>

          {/* SLIDERS */}
          <div style={{ marginBottom: '32px' }}>
            <div style={{ fontSize: '0.85rem', fontWeight: 800, color: '#fbbf24', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '16px' }}>Your Team</div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '16px' }}>
              {[
                { label: 'Architects', value: roiArchitects, set: v => { setRoiArchitects(v); setRoiTouched(true); }, min: 1, max: 50, display: v => `${v}`, sub: 'people', color: '#38bdf8' },
                { label: 'Hourly Rate', value: roiRate, set: v => { setRoiRate(v); setRoiTouched(true); }, min: 80, max: 300, display: v => `$${v}`, sub: '/ hr', color: '#a78bfa' },
                { label: 'Projects / Year', value: roiProjects, set: v => { setRoiProjects(v); setRoiTouched(true); }, min: 1, max: 100, display: v => `${v}`, sub: 'projects', color: '#fb923c' },
              ].map(s => (
                <div key={s.label} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '18px', padding: '20px 22px 16px' }}>
                  <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#e2e8f0', marginBottom: '8px' }}>{s.label}</div>
                  <div style={{ fontSize: isMobile ? '2.8rem' : '3.4rem', fontWeight: 900, color: s.color, lineHeight: 1, marginBottom: '4px', letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums' }}>
                    {s.display(s.value)}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '16px' }}>{s.sub}</div>
                  <input type="range" min={s.min} max={s.max} value={s.value}
                    onChange={e => s.set(Number(e.target.value))}
                    style={{ width: '100%', accentColor: s.color, cursor: 'pointer' }}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* TASK TOGGLES */}
          <div style={{ marginBottom: '48px' }}>
            <div style={{ fontSize: '0.85rem', fontWeight: 800, color: '#fbbf24', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '16px' }}>Tasks the Agent Automates — Select all that apply</div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(6, 1fr)', gap: '12px' }}>
              {Object.entries(TASK_DEFS).map(([k, t], idx) => {
                const on = roiTasks[k];
                const pct = Math.round(((t.trad - t.agent) / t.trad) * 100);
                const colors = ['#38bdf8', '#a78bfa', '#34d399', '#fbbf24', '#f87171', '#38bdf8'];
                const color = colors[idx % colors.length];
                return (
                  <motion.button key={k}
                    onClick={() => { setRoiTasks(prev => ({ ...prev, [k]: !prev[k] })); setRoiTouched(true); }}
                    whileHover={{ scale: 1.03, y: -2 }}
                    whileTap={{ scale: 0.97 }}
                    style={{
                      padding: '20px 12px',
                      borderRadius: '20px',
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: on ? color : 'rgba(255,255,255,0.08)',
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '10px',
                      minHeight: '120px',
                      justifyContent: 'center',
                      background: on
                        ? `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)`
                        : 'rgba(255,255,255,0.02)',
                      boxShadow: on ? `0 8px 32px ${color}30, inset 0 1px 0 ${color}20` : '0 2px 8px rgba(0,0,0,0.15)',
                      transition: 'all 0.25s ease',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <div style={{ position: 'absolute', top: '10px', right: '10px', width: '24px', height: '24px', borderRadius: '8px', border: `2px solid ${on ? color : 'rgba(255,255,255,0.15)'}`, background: on ? color : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {on && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
                    </div>
                    {on && (
                      <div style={{ position: 'absolute', top: -20, right: -20, width: '60px', height: '60px', background: `${color}20`, borderRadius: '50%', filter: 'blur(20px)' }} />
                    )}
                    <div style={{
                      width: '48px', height: '48px', borderRadius: '14px',
                      background: on ? `linear-gradient(135deg, ${color}30, ${color}15)` : 'rgba(255,255,255,0.05)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: on ? `1px solid ${color}40` : '1px solid rgba(255,255,255,0.08)',
                      boxShadow: on ? `0 4px 16px ${color}30` : 'none',
                    }}>
                      {React.cloneElement(t.icon, { size: 22, color: on ? color : '#64748b' })}
                    </div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: on ? '#ffffff' : '#94a3b8', lineHeight: 1.3, textAlign: 'center' }}>{t.label}</div>
                    <motion.div animate={{ opacity: on ? 1 : 0.3 }}
                      style={{ fontSize: '0.8rem', fontWeight: 800, color: color }}>
                      -{pct}%
                    </motion.div>
                  </motion.button>
                );
              })}
            </div>
          </div>

          </div>
      </section>

            
{/* PLATFORM INTERFACE */}
      <section style={{ padding: isMobile ? '48px 0' : '80px 0', background: '#0f172a', color: 'white', borderTop: '1px solid #1e293b' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <div style={{ textAlign: 'center', margin: '0 auto', maxWidth: '800px' }}>
            <span style={{ color: '#38bdf8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', fontSize: '0.85rem' }}>Eagentix Platform Interface</span>
            <h2 style={{ fontSize: isMobile ? '1.6rem' : '2.5rem', fontWeight: 800, color: 'white', marginTop: '16px', marginBottom: '16px', letterSpacing: '-0.02em' }}>Conversational Intelligence Meets Infrastructure</h2>
            <p style={{ fontSize: '1.1rem', color: '#cbd5e1', lineHeight: 1.6 }}>Experience a fluid workspace where natural language requirements instantly compile into visual topologies and deterministically generated Infrastructure-as-Code.</p>
          </div>
          <div style={{ marginTop: '64px' }}>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              style={{ borderRadius: '28px', border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden', boxShadow: 'rgba(0,0,0,0.5) 0 25px 50px -12px', background: 'linear-gradient(180deg, rgba(30,41,59,0.98) 0%, rgba(15,23,42,0.98) 100%)' }}
            >
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(320px, 0.95fr) minmax(0, 1.25fr)' }}>
                <div style={{ padding: '36px 32px', borderRight: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '28px' }}>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={platformFeatures[activePlatformFeature].title}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -12 }}
                      transition={{ duration: 0.28 }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', padding: '7px 12px', borderRadius: '999px', background: 'rgba(56,189,248,0.12)', border: '1px solid rgba(56,189,248,0.18)', color: '#7dd3fc', fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                        {platformFeatures[activePlatformFeature].tag}
                      </span>
                      <h3 style={{ fontSize: '1.7rem', fontWeight: 800, color: '#f8fafc', margin: '18px 0 12px', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                        {platformFeatures[activePlatformFeature].title}
                      </h3>
                      <p style={{ color: '#dbeafe', lineHeight: 1.75, fontSize: '1.02rem', margin: 0 }}>
                        {platformFeatures[activePlatformFeature].desc}
                      </p>
                    </motion.div>
                  </AnimatePresence>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    {platformFeatures.map((feature, index) => (
                      <motion.button
                        key={feature.title}
                        type="button"
                        aria-label={`Show ${feature.title}`}
                        onClick={() => setActivePlatformFeature(index)}
                        whileTap={{ scale: 0.94 }}
                        style={{
                          width: index === activePlatformFeature ? '36px' : '10px',
                          height: '10px',
                          borderRadius: '999px',
                          border: 'none',
                          background: index === activePlatformFeature ? 'linear-gradient(90deg, #38bdf8 0%, #2563eb 100%)' : 'rgba(148,163,184,0.45)',
                          cursor: 'pointer',
                          transition: 'all 0.35s ease',
                          padding: 0,
                        }}
                      />
                    ))}
                  </div>
                </div>
                <div style={{ minWidth: 0, overflow: 'hidden', position: 'relative', background: '#1e293b' }}>
                  <motion.div
                    animate={{ x: `-${activePlatformFeature * (100 / platformFeatures.length)}%` }}
                    transition={{ duration: 0.95, ease: [0.22, 1, 0.36, 1] }}
                    style={{ display: 'flex', width: `${platformFeatures.length * 100}%` }}
                  >
                    {platformFeatures.map((feature) => (
                      <div key={feature.title} style={{ width: `${100 / platformFeatures.length}%`, flexShrink: 0 }}>
                        <img alt={feature.alt} src={feature.img} style={{ width: '100%', height: '100%', minHeight: '460px', objectFit: 'cover', display: 'block' }} />
                      </div>
                    ))}
                  </motion.div>
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(15,23,42,0.18) 0%, rgba(15,23,42,0) 16%, rgba(15,23,42,0) 84%, rgba(15,23,42,0.18) 100%)', pointerEvents: 'none' }} />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* WELL-ARCHITECTED SPECIFICITY */}
      <section style={{ padding: isMobile ? '48px 0' : '82px 0', background: 'white' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(320px, 0.9fr) minmax(0, 1.1fr)', gap: '26px', alignItems: 'start' }}>
            <div>
              <span style={{ color: '#0071e3', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', fontSize: '0.85rem' }}>Framework Specificity</span>
              <h2 style={{ fontSize: isMobile ? '1.6rem' : '2.55rem', fontWeight: 800, color: '#0f172a', margin: '16px 0 16px', letterSpacing: '-0.03em', lineHeight: 1.04 }}>
                Multi-cloud Well-Architected should not sound vague
              </h2>
              <p style={{ fontSize: '1.05rem', color: '#475569', lineHeight: 1.78, margin: 0 }}>
                Each cloud provider has its own Well-Architected framework with distinct pillars and review criteria. The agent applies provider-aware assessments — not a flattened generic rubric — so findings are actionable against the actual platform you're building on.
              </p>
            </div>
            <div style={{ display: 'grid', gap: '16px' }}>
              {frameworkSpecificity.map((item) => (
                <div key={item} style={{ padding: '22px 24px', borderRadius: '24px', background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)', border: '1px solid rgba(148,163,184,0.16)', boxShadow: 'rgba(15,23,42,0.05) 0 18px 30px -24px', color: '#334155', fontSize: '0.97rem', fontWeight: 600, lineHeight: 1.68 }}>
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BUYERS + WORKFLOW */}
      <section style={{ padding: isMobile ? '48px 0' : '86px 0', background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)', borderTop: '1px solid #eef2f7', borderBottom: '1px solid #eef2f7' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(320px, 0.92fr) minmax(0, 1.15fr)', gap: '28px', alignItems: 'start' }}>
            <div>
              <span style={{ color: '#0071e3', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', fontSize: '0.85rem' }}>Who It Is For</span>
              <h2 style={{ fontSize: isMobile ? '1.6rem' : '2.75rem', fontWeight: 800, color: '#0f172a', margin: '16px 0 18px', letterSpacing: '-0.04em', lineHeight: 1.02 }}>
                Built for teams making <span style={{ color: '#2563eb' }}>expensive</span> cloud decisions
              </h2>
              <p style={{ fontSize: '1.08rem', color: '#475569', lineHeight: 1.8, margin: 0, maxWidth: '560px' }}>
                For architecture leaders, platform teams, and cloud programs that need faster, better-documented decisions around migration, security, reliability, and cost posture — without waiting for a consulting cycle to start.
              </p>
              <div style={{ marginTop: '26px', display: 'grid', gap: '16px' }}>
                {targetPersonas.map((persona) => (
                  <div key={persona.title} style={{ padding: '20px 22px', borderRadius: '22px', background: 'white', border: '1px solid rgba(148,163,184,0.16)', boxShadow: 'rgba(15,23,42,0.06) 0 16px 32px -24px' }}>
                    <h3 style={{ margin: 0, fontSize: '1.08rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>{persona.title}</h3>
                    <p style={{ margin: '10px 0 0', color: '#475569', lineHeight: 1.72, fontSize: '0.96rem' }}>{persona.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ borderRadius: '30px', overflow: 'hidden', border: '1px solid rgba(148,163,184,0.14)', background: 'linear-gradient(145deg, #162033 0%, #1e3a8a 100%)', color: 'white', boxShadow: 'rgba(15,23,42,0.16) 0 28px 56px -28px' }}>
              <div style={{ padding: '30px 30px 16px' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', padding: '7px 12px', borderRadius: '999px', background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(147,197,253,0.18)', color: '#93c5fd', fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  How It Works
                </span>
                <h3 style={{ fontSize: '2rem', fontWeight: 800, margin: '18px 0 10px', letterSpacing: '-0.03em' }}>From architecture question to delivery-ready output</h3>
                <p style={{ margin: 0, color: '#e2e8f0', lineHeight: 1.75, fontSize: '1rem' }}>
                  The workflow is now explicit: conversation, constraint discovery, design generation, and escalation when the infrastructure decision needs human review.
                </p>
              </div>
              <div style={{ padding: '10px 30px 30px', display: 'grid', gap: '14px' }}>
                {workflowSteps.map((step) => (
                  <div key={step.step} style={{ display: 'grid', gridTemplateColumns: '64px minmax(0, 1fr)', gap: '16px', alignItems: 'start', padding: '16px 0', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                    <div style={{ width: '64px', height: '64px', borderRadius: '18px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      <span style={{ color: '#7dd3fc', fontWeight: 800, fontSize: '0.72rem', letterSpacing: '0.06em' }}>{step.step}</span>
                      {step.cloud}
                    </div>
                    <div>
                      <h4 style={{ margin: 0, fontSize: '1.08rem', fontWeight: 800, color: '#f8fafc' }}>{step.title}</h4>
                      <p style={{ margin: '8px 0 0', color: '#dbeafe', lineHeight: 1.72, fontSize: '0.96rem' }}>{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 24x7 BANNER */}
      <section style={{ padding: '40px 0', background: 'linear-gradient(90deg, #111827 0%, #1e3a8a 100%)', borderTop: '1px solid #1e293b', borderBottom: '1px solid #1e293b' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', flexWrap: 'wrap', textAlign: 'center' }}>
          <div style={{ width: '12px', height: '12px', background: '#34d399', borderRadius: '50%', boxShadow: '#34d399 0 0 12px' }} />
          <p style={{ fontSize: '1.25rem', color: 'white', margin: 0, fontWeight: 500, paddingRight: '12px' }}>
            <span style={{ fontWeight: 800, color: '#60a5fa' }}>24x7 Architecture Advisory</span>
          </p>
          <div style={{ width: '2px', height: '24px', background: 'rgba(255,255,255,0.2)' }} />
          <p style={{ fontSize: '1.15rem', color: '#e2e8f0', margin: 0, paddingLeft: '12px', fontWeight: 400 }}>Use the workspace anytime to work through WAF, IaC, architecture diagrams, multi-cloud, hybrid, security, migration, and platform architecture decisions.</p>
        </div>
      </section>

      {/* AGENT CAPABILITIES */}
      <section id="capabilities" style={{ padding: isMobile ? '48px 0' : '100px 0', background: 'radial-gradient(circle at top, #dbeafe 0%, #eff6ff 18%, #f8fafc 42%, #f8fafc 100%)', borderTop: '1px solid #e2e8f0' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(320px, 0.95fr) minmax(0, 1.25fr)', gap: '32px', alignItems: 'start', marginBottom: '40px' }}>
            <div style={{ position: 'sticky', top: '110px' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderRadius: '999px', background: 'rgba(255,255,255,0.82)', border: '1px solid rgba(148,163,184,0.2)', boxShadow: 'rgba(15,23,42,0.06) 0 12px 28px -18px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '999px', background: '#2563eb', boxShadow: '0 0 18px rgba(37,99,235,0.45)' }} />
                <span style={{ color: '#2563eb', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', fontSize: '0.76rem' }}>Architecture Advisory + Capabilities</span>
              </div>
              <h2 style={{ fontSize: 'clamp(2.8rem, 5vw, 4.8rem)', fontWeight: 900, color: '#0f172a', marginTop: '24px', marginBottom: '20px', letterSpacing: '-0.05em', lineHeight: 0.94 }}>
                Go to the architect for <span style={{ color: '#2563eb' }}>real cloud</span> decisions
              </h2>
              <p style={{ fontSize: '1.15rem', color: '#475569', lineHeight: 1.8, maxWidth: '560px', marginBottom: '28px' }}>
                Bring real platform questions to the agent, then move straight into the technical systems that generate, validate, and operationalize the architecture.
              </p>
              <div style={{ background: 'linear-gradient(145deg, #0f172a 0%, #1e3a8a 100%)', color: 'white', borderRadius: '28px', padding: '28px', boxShadow: 'rgba(15,23,42,0.28) 0 30px 60px -28px' }}>
                <p style={{ margin: 0, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.16em', color: '#93c5fd', fontWeight: 800 }}>Always-On Architecture Desk</p>
                <p style={{ margin: '16px 0 0', fontSize: '1.12rem', lineHeight: 1.8, color: '#dbeafe' }}>
                  Architecture reviews, WAF guidance, IaC generation, hybrid planning, diagrams, stakeholder briefs, and ADRs in one workflow.
                </p>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: '22px' }}>
              {unifiedCapabilities.map((item, index) => (
                <div
                  key={item.title}
                  style={{
                    background: index === 0
                      ? 'linear-gradient(145deg, #111827 0%, #1e3a8a 100%)'
                      : 'linear-gradient(180deg, rgba(255,255,255,0.94) 0%, rgba(248,250,252,0.96) 100%)',
                    border: index === 0 ? '1px solid rgba(96,165,250,0.18)' : '1px solid rgba(148,163,184,0.14)',
                    borderRadius: '24px',
                    padding: '24px',
                    boxShadow: 'rgba(15,23,42,0.08) 0 24px 40px -30px',
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: index === 0 ? '280px' : 'auto',
                    gridColumn: index === 0 ? '1 / -1' : 'auto',
                  }}
                >
                  <div style={{ position: 'absolute', inset: 0, background: item.bg, opacity: index === 0 ? 0.24 : 0.2, filter: 'blur(60px)', transform: 'translate(25%, -35%)' }} />
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <div style={{ width: '52px', height: '52px', borderRadius: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: item.tint, background: item.bg, border: '1px solid rgba(148,163,184,0.16)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.65)' }}>
                      {item.icon}
                    </div>
                    <h3 style={{ fontSize: index === 0 ? '1.65rem' : '1.18rem', fontWeight: 800, color: index === 0 ? 'white' : '#111827', margin: '18px 0 10px', letterSpacing: '-0.02em', lineHeight: 1.15 }}>{item.title}</h3>
                    <p style={{ fontSize: index === 0 ? '1.04rem' : '0.98rem', color: index === 0 ? '#dbeafe' : '#475569', lineHeight: 1.75, margin: 0 }}>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DELIVERABLES + DIFFERENTIATION */}
      <section style={{ padding: isMobile ? '48px 0' : '88px 0', background: '#ffffff', borderTop: '1px solid #eef2f7' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(320px, 0.92fr) minmax(0, 1.15fr)', gap: '28px', alignItems: 'start' }}>
            <div style={{ position: 'sticky', top: '110px' }}>
              <span style={{ color: '#0071e3', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', fontSize: '0.85rem' }}>Deliverables + Decision Model</span>
              <h2 style={{ fontSize: isMobile ? '1.6rem' : '2.7rem', fontWeight: 800, color: '#0f172a', margin: '16px 0 18px', letterSpacing: '-0.04em', lineHeight: 1.03 }}>
                What the architect should actually hand back
              </h2>
              <p style={{ fontSize: '1.08rem', color: '#475569', lineHeight: 1.8, margin: 0, maxWidth: '560px' }}>
                This is not just a conversational agent for cloud opinions. It is an architecture workbench that produces review artifacts, implementation guidance, and decision-ready deliverables you can act on.
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: '16px' }}>
              {deliverables.map((item) => (
                <div key={item} style={{ padding: '20px 20px', borderRadius: '22px', background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)', border: '1px solid rgba(148,163,184,0.16)', boxShadow: 'rgba(15,23,42,0.05) 0 18px 30px -24px', color: '#0f172a', fontSize: '0.96rem', fontWeight: 700, lineHeight: 1.5 }}>
                  {item}
                </div>
              ))}
            </div>
          </div>
          <div style={{ marginTop: '34px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, minmax(0, 1fr))', gap: '18px' }}>
            {decisionBenefits.map((item) => (
              <div key={item.title} style={{ padding: '24px', borderRadius: '24px', background: '#162033', color: 'white', boxShadow: 'rgba(15,23,42,0.12) 0 22px 40px -26px' }}>
                <h3 style={{ margin: 0, fontSize: '1.18rem', fontWeight: 800, letterSpacing: '-0.02em' }}>{item.title}</h3>
                <p style={{ margin: '12px 0 0', color: '#e2e8f0', lineHeight: 1.75, fontSize: '0.96rem' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NFR SAFETY NET */}
      <section style={{ padding: isMobile ? '48px 0' : '80px 0', background: 'white' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <div style={{ background: '#111827', borderRadius: '32px', padding: isMobile ? '48px 24px' : '80px 40px', color: 'white', textAlign: 'center', position: 'relative', overflow: 'hidden', boxShadow: 'rgba(0,0,0,0.25) 0 25px 50px -12px' }}>
            <div style={{ position: 'absolute', top: '-150px', left: '-50px', width: '400px', height: '400px', background: '#0071e3', filter: 'blur(120px)', opacity: 0.4, borderRadius: '50%', display: isMobile ? 'none' : 'block' }} />
            <div style={{ position: 'absolute', bottom: '-150px', right: '-50px', width: '400px', height: '400px', background: '#8b5cf6', filter: 'blur(120px)', opacity: 0.3, borderRadius: '50%', display: isMobile ? 'none' : 'block' }} />
            <div style={{ position: 'relative', zIndex: 1, maxWidth: '850px', margin: '0 auto' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', padding: '6px 16px', background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '30px', fontSize: '0.85rem', fontWeight: 600, marginBottom: '24px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Built for Enterprise Compliance</div>
              <h2 style={{ fontSize: isMobile ? '1.6rem' : '3rem', marginBottom: '32px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1, color: 'white' }}>Non-Functional Requirement <br />(NFR) Safety Net</h2>
              <p style={{ fontSize: '1.25rem', lineHeight: 1.7, color: '#d1d5db' }}>
                The agent doesn't just listen for keywords; it understands architectural intent. The built-in safety net actively isolates terms like <span style={{ color: '#93c5fd', fontWeight: 600 }}>"zero-downtime," "HIPAA," "active-active," "SLO,"</span> and <span style={{ color: '#93c5fd', fontWeight: 600 }}>"chaos drill"</span> directly out of informal discourse — translating them into HA/DR topologies, error-budget policies, Policy-as-Code guardrails, OpenTelemetry instrumentation, and compliance-ready IaC templates from the start.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* HUMAN ESCALATION */}
      <section style={{ padding: isMobile ? '48px 0' : '70px 0 96px', background: 'linear-gradient(180deg, #eff6ff 0%, #ffffff 100%)' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <div style={{ borderRadius: '32px', overflow: 'hidden', border: '1px solid rgba(148,163,184,0.18)', background: 'linear-gradient(135deg, #111827 0%, #1d4ed8 100%)', color: 'white', boxShadow: 'rgba(15,23,42,0.18) 0 30px 60px -32px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(320px, 0.95fr) minmax(0, 1.05fr)', gap: '0' }}>
              <div style={{ padding: isMobile ? '28px 20px' : '38px 34px', borderRight: isMobile ? 'none' : '1px solid rgba(255,255,255,0.08)', borderBottom: isMobile ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', padding: '7px 12px', borderRadius: '999px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#bfdbfe', fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  Human-In-The-Loop
                </span>
                <h2 style={{ fontSize: isMobile ? '1.4rem' : '2.15rem', fontWeight: 800, margin: '18px 0 12px', letterSpacing: '-0.03em', lineHeight: 1.08, color: '#f8fafc' }}>
                  The agent should not pretend to replace judgment when the stakes are high
                </h2>
                <p style={{ margin: 0, color: '#e2e8f0', lineHeight: 1.78, fontSize: '1rem' }}>
                  For architecture choices tied to regulatory exposure, business continuity, enterprise exceptions, or large budget commitments, the workflow should escalate into human review. That makes the system more trustworthy, not less autonomous.
                </p>
              </div>
              <div style={{ padding: isMobile ? '28px 20px' : '38px 34px', display: 'grid', gap: '14px' }}>
                {[
                  'Escalate for compliance-sensitive workloads like HIPAA, PCI, or internal policy exceptions.',
                  'Escalate for large migration cutovers, active-active topology choices, and material resilience tradeoffs.',
                  'Escalate when the architecture affects shared platform standards, cross-team governance, or executive sign-off.',
                ].map((item) => (
                  <div key={item} style={{ padding: '16px 18px', borderRadius: '18px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', color: '#eff6ff', fontSize: '0.95rem', fontWeight: 600, lineHeight: 1.65 }}>
                    {item}
                  </div>
                ))}
                <a href="https://app.eagentix.com/register?reason=Requesting%20access%20to%20Cloud%20Architect%20Agent" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', alignSelf: 'start', marginTop: '8px', background: 'white', color: '#0f172a', padding: '14px 22px', borderRadius: '12px', fontWeight: 800, textDecoration: 'none', boxShadow: 'rgba(255,255,255,0.18) 0 12px 24px -16px' }}>
                  Talk to the Architect
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      <AnimatePresence>
        {expandedArchitecture && (() => {
          const currentIndex = architectures.findIndex(a => a.id === expandedArchitecture.id);
          const goTo = (idx) => setExpandedArchitecture(architectures[(idx + architectures.length) % architectures.length]);
          return (
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-label={expandedArchitecture.title}
              onClick={() => setExpandedArchitecture(null)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22 }}
              style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(2,6,23,0.88)', backdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
            >
              <motion.div
                onClick={(e) => e.stopPropagation()}
                initial={{ opacity: 0, y: 24, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 12, scale: 0.98 }}
                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                style={{ width: 'min(1400px, 100%)', height: '90vh', display: 'flex', flexDirection: 'column', background: '#0f172a', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 32px 80px rgba(0,0,0,0.6)', overflow: 'hidden' }}
              >
                {/* header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ padding: '4px 10px', borderRadius: '999px', background: 'linear-gradient(90deg,#0ea5e9,#2563eb)', color: 'white', fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.08em' }}>
                      {expandedArchitecture.tag || 'Diagram'}
                    </span>
                    <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: 'white' }}>{expandedArchitecture.title}</h3>
                    <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>{currentIndex + 1} / {architectures.length}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {/* zoom hint */}
                    <span style={{ fontSize: '0.72rem', color: '#475569', fontWeight: 600 }}>Scroll to zoom</span>
                    <motion.button type="button" onClick={() => setExpandedArchitecture(null)} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                      style={{ border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.06)', color: 'white', borderRadius: '999px', padding: '8px 16px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}>
                      ✕ Close
                    </motion.button>
                  </div>
                </div>

                {/* image area with zoom */}
                <div style={{ position: 'relative' }}>
                  <ZoomableImage
                    src={expandedArchitecture.src || `/images/architectures/${expandedArchitecture.id}.png`}
                    alt={expandedArchitecture.title}
                  />
                  {/* left arrow */}
                  <motion.button
                    type="button"
                    aria-label="Previous diagram"
                    onClick={() => goTo(currentIndex - 1)}
                    whileHover={{ scale: 1.1, x: -2 }}
                    whileTap={{ scale: 0.93 }}
                    style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', zIndex: 20, width: '48px', height: '48px', borderRadius: '50%', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(15,23,42,0.75)', backdropFilter: 'blur(8px)', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.4)' }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                  </motion.button>
                  {/* right arrow */}
                  <motion.button
                    type="button"
                    aria-label="Next diagram"
                    onClick={() => goTo(currentIndex + 1)}
                    whileHover={{ scale: 1.1, x: 2 }}
                    whileTap={{ scale: 0.93 }}
                    style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', zIndex: 20, width: '48px', height: '48px', borderRadius: '50%', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(15,23,42,0.75)', backdropFilter: 'blur(8px)', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.4)' }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                  </motion.button>
                </div>

                {/* footer nav */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderTop: '1px solid rgba(255,255,255,0.08)', flexShrink: 0, background: '#0f172a' }}>
                  <motion.button type="button" onClick={() => goTo(currentIndex - 1)} whileHover={{ x: -2 }} whileTap={{ scale: 0.95 }}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.06)', color: 'white', borderRadius: '12px', padding: '10px 18px', fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                    {architectures[(currentIndex - 1 + architectures.length) % architectures.length].title}
                  </motion.button>

                  {/* step pills */}
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {architectures.map((a, i) => (
                      <motion.button key={a.id} type="button" onClick={() => goTo(i)} whileTap={{ scale: 0.9 }}
                        animate={{ width: i === currentIndex ? 28 : 8 }}
                        transition={{ duration: 0.3 }}
                        style={{ height: '8px', borderRadius: '999px', border: 'none', background: i === currentIndex ? 'linear-gradient(90deg,#0ea5e9,#2563eb)' : 'rgba(255,255,255,0.2)', cursor: 'pointer', padding: 0, flexShrink: 0 }}
                      />
                    ))}
                  </div>

                  <motion.button type="button" onClick={() => goTo(currentIndex + 1)} whileHover={{ x: 2 }} whileTap={{ scale: 0.95 }}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.06)', color: 'white', borderRadius: '12px', padding: '10px 18px', fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer' }}>
                    {architectures[(currentIndex + 1) % architectures.length].title}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>

      {/* CELEBRATION OVERLAY */}
      <AnimatePresence>
        {showCelebration && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(2,8,23,0.92)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
            onClick={() => setShowCelebration(false)}
          >
            {confettiActive && (
              <ReactConfetti
                width={windowWidth}
                height={windowHeight}
                numberOfPieces={350}
                recycle={false}
                colors={['#38bdf8', '#0071e3', '#fbbf24', '#34d399', '#a78bfa', '#ffffff']}
              />
            )}
            <motion.div
              initial={{ scale: 0.7, opacity: 0, y: 40 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 280, damping: 22 }}
              onClick={e => e.stopPropagation()}
              style={{ background: 'linear-gradient(145deg, #0f2744 0%, #0c1f3f 100%)', border: '1px solid rgba(56,189,248,0.3)', borderRadius: '28px', padding: isMobile ? '36px 24px' : '52px 60px', maxWidth: '560px', width: '100%', textAlign: 'center', boxShadow: '0 0 80px rgba(56,189,248,0.2)' }}
            >
              <div style={{ fontSize: isMobile ? '3rem' : '4rem', marginBottom: '16px' }}>🎉</div>
              <p style={{ fontSize: '0.82rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '12px' }}>Your team would save</p>
              <motion.div
                initial={{ scale: 0.5 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 300 }}
                style={{ fontSize: isMobile ? '3.5rem' : '5rem', fontWeight: 900, color: '#38bdf8', lineHeight: 1, marginBottom: '8px' }}
              >
                {fmtLive(totalSavings)}
              </motion.div>
              <p style={{ fontSize: isMobile ? '1.2rem' : '1.5rem', fontWeight: 700, color: '#e2e8f0', marginBottom: '8px' }}>
                and <span style={{ color: '#fbbf24' }}>{weeksSavedPerYear} weeks</span> this year
              </p>
              
              <p style={{ fontSize: '0.9rem', color: '#cbd5e1', marginBottom: '32px' }}>
                That's {roiArchitects} architect{roiArchitects > 1 ? 's' : ''} freed up for real innovation — not boilerplate.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <a
                  href="https://app.eagentix.com/register?reason=Requesting%20access%20to%20Cloud%20Architect%20Agent"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'block', padding: '16px', borderRadius: '14px', background: 'linear-gradient(135deg, #38bdf8 0%, #0071e3 100%)', color: '#fff', fontWeight: 800, fontSize: '1rem', textDecoration: 'none', boxShadow: '0 4px 20px rgba(56,189,248,0.3)' }}
                >
                  Lock In These Savings → Book a Demo
                </a>
                <button
                  onClick={() => setShowCelebration(false)}
                  style={{ padding: '12px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#cbd5e1', fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer' }}
                >
                  Recalculate
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}

export default CloudArchitect;
